/**
 * Personalisation fields (name / number / letter) on the product page.
 *
 * Two jobs:
 *
 * 1. Case handling. Names are submitted uppercase by default because that is
 *    how they are printed; the toggle lets a shopper opt into the case they
 *    typed. The transform is applied to the value, not with text-transform,
 *    because CSS only changes the rendering -- the cart would still receive
 *    whatever was typed.
 *
 * 2. Dropping empty optional fields. An untouched input still submits as an
 *    empty property, which shows as a blank row in the cart. Disabling it at
 *    submit time keeps it out of the payload without clearing what the
 *    shopper sees.
 */
class PersonalizationFields extends HTMLElement {
  connectedCallback() {
    this.nameInput = this.querySelector('[data-uppercase]');
    this.caseToggle = this.querySelector('[data-case-toggle]');
    this.form = this.closest('form') || this.ownerDocument.getElementById(this.formId);

    if (this.nameInput) {
      this.nameInput.addEventListener('input', this.handleNameInput.bind(this));
      this.applyCase();
    }

    if (this.caseToggle) {
      this.caseToggle.addEventListener('change', this.applyCase.bind(this));
    }

    this.bindForm();
  }

  get formId() {
    const field = this.querySelector('[form]');
    return field ? field.getAttribute('form') : null;
  }

  /** True when names should be forced to capitals. */
  get forcesUppercase() {
    if (!this.nameInput) return false;
    if (this.nameInput.dataset.uppercase !== 'true') return false;
    // The toggle opts OUT of the forced case, so checked means "keep my case".
    return !(this.caseToggle && this.caseToggle.checked);
  }

  handleNameInput() {
    if (!this.forcesUppercase) return;
    this.applyCase();
  }

  applyCase() {
    if (!this.nameInput || !this.forcesUppercase) return;
    const upper = this.nameInput.value.toUpperCase();
    if (upper === this.nameInput.value) return;

    // Preserve the caret: reassigning value moves it to the end, which makes
    // mid-string editing impossible.
    const start = this.nameInput.selectionStart;
    const end = this.nameInput.selectionEnd;
    this.nameInput.value = upper;
    if (start !== null) {
      try {
        this.nameInput.setSelectionRange(start, end);
      } catch (e) {
        /* setSelectionRange throws on input types that do not support it */
      }
    }
  }

  bindForm() {
    const id = this.formId;
    const form = id ? this.ownerDocument.getElementById(id) : this.closest('form');
    if (!form || form.dataset.personalizationBound === 'true') return;
    form.dataset.personalizationBound = 'true';
    form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  handleSubmit() {
    this.querySelectorAll('input[name^="properties"]').forEach((field) => {
      // A checkbox already submits nothing when unchecked, so it needs no help.
      if (field.type === 'checkbox') return;

      // "None" is a real choice but carries no printed letter, so the radio is
      // disabled to keep properties[Letter] off the order. Other radios are
      // left alone -- an unselected one submits nothing anyway.
      if (field.type === 'radio') {
        if (field.hasAttribute('data-letter-none') && field.checked) {
          field.disabled = true;
          window.setTimeout(() => {
            field.disabled = false;
          }, 0);
        }
        return;
      }

      const empty = field.value.trim() === '';
      field.disabled = empty;
      // Re-enable after the submit handler runs so the field stays usable if
      // the add-to-cart is intercepted rather than navigating away.
      if (empty) {
        window.setTimeout(() => {
          field.disabled = false;
        }, 0);
      }
    });
  }
}

if (!customElements.get('personalization-fields')) {
  customElements.define('personalization-fields', PersonalizationFields);
}
